<template>
  <div>
    <v-dialog v-model="dialog" max-width="900px" scrollable>
      <v-card v-if="dialog">
        <v-card-title class="headline">
          {{ $t('job-manager.job-details') }}
          <v-spacer></v-spacer>
          <v-btn icon @click="close" small><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text>
          <!-- Header -->
          <detail-header :item="headerItem" />

          <!-- Workflow -->
          <div v-if="isWorkflow">
            <workflow-steps :steps="transitions" @show-step-details="showTransitionDetails" />
          </div>

          <!-- Unitary -->
          <div v-else>
            <!-- Attempt selector -->
            <attempt-selector
                v-if="executionAttemptsCount > 1"
                v-model="selectedAttempt"
                :max-attempts="executionAttemptsCount"
                class="mb-2"
            />

            <!-- === Bloc IDs + métriques AU-DESSUS des onglets (seulement unitaire) === -->
            <div v-if="currentExec" class="mb-2">
              <v-row>
                <v-col cols="12" md="6">
                  <div class="detail-item">
                    <div class="detail-label">Wait time:</div>
                    <div class="detail-value">{{ humanDuration(waitTimeSec) || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">{{ $t('job-manager.duration') }}:</div>
                    <div class="detail-value">{{ humanDuration(durationSec) || '-' }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="6">
                  <div class="detail-item">
                    <div class="detail-label">{{ $t('job-manager.attempt') }}:</div>
                    <div class="detail-value">{{ selectedAttempt }} / {{ executionAttemptsCount }}</div>
                  </div>

                  <div class="detail-item">
                    <div class="detail-label">Execution ID:</div>
                    <div class="detail-value d-flex align-center">
                      <code class="mono">{{ truncateId(currentExec && currentExec.id) }}</code>
                      <v-btn icon small @click="copy(currentExec && currentExec.id)" :disabled="!currentExec || !currentExec.id" :title="tCopy">
                        <v-icon small>mdi-content-copy</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <div class="detail-item" v-if="currentExec && currentExec.fromExecutionId">
                    <div class="detail-label">From Execution ID:</div>
                    <div class="detail-value d-flex align-center">
                      <code class="mono">{{ truncateId(currentExec.fromExecutionId) }}</code>
                      <v-btn icon small @click="copy(currentExec.fromExecutionId)" :title="tCopy">
                        <v-icon small>mdi-content-copy</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Onglets -->
            <tab-switcher v-model="activeTab" :tabs="unitaryTabs">
              <!-- CONTEXT -->
              <template v-slot:context>
                <div>
                  <h3 class="mb-2">{{ $t('job-manager.input-context') }}</h3>
                  <context-viewer :context="unitaryInputContext" />

                  <h3 class="mt-4 mb-2">{{ $t('job-manager.output-context') }}</h3>
                  <context-viewer :context="unitaryOutputContext" />
                </div>
              </template>

              <!-- LOGS (pas d'IDs ici, ils sont déjà AU-DESSUS) -->
              <template v-slot:logs>
                <div v-if="currentExecutionId">
                  <log-viewer
                      :key="currentExecutionId"
                      :execution-id="currentExecutionId"
                  />
                </div>
                <div v-else class="pa-4 grey--text">
                  {{ $t('job-manager.no-execution-selected') }}
                </div>
              </template>

              <!-- BRUT : même composant que Transition -->
              <template v-slot:raw>
                <raw-json-panel :value="currentExec" :title="$t('job-manager.raw-json')" />
              </template>
            </tab-switcher>
          </div>

          <v-card-actions class="mt-4">
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="close" small>{{ $t('close') }}</v-btn>
          </v-card-actions>
        </v-card-text>
      </v-card>
    </v-dialog>

    <transition-detail-modal v-model="showTransitionModal" :transition="selectedTransition" />
  </div>
</template>

<script>
import DetailHeader from '../elements/DetailHeader.vue';
import TabSwitcher from '../elements/TabSwitcher.vue';
import ContextViewer from '../elements/ContextViewer.vue';
import LogViewer from '../elements/LogViewer.vue';
import WorkflowSteps from '../elements/WorkflowSteps.vue';
import AttemptSelector from '../elements/AttemptSelector.vue';
import TransitionDetailModal from './TransitionDetailModal.vue';
import RawJsonPanel from '../elements/RawJsonPanel.vue';

export default {
  name: 'JobDetailsModal',
  components: {
    DetailHeader, TabSwitcher, ContextViewer, LogViewer,
    WorkflowSteps, AttemptSelector, TransitionDetailModal, RawJsonPanel
  },
  props: {
    value: { type: Boolean, default: false },
    job:   { type: Object }
  },
  data() {
    return {
      activeTab: 0,
      showTransitionModal: false,
      selectedTransition: {},
      selectedAttempt: 1
    };
  },
  watch: {
    job: {
      immediate: true,
      handler() {
        const execs = Array.isArray(this.job?.executions) ? this.job.executions : [];
        this.selectedAttempt = execs.length ? execs.length : 1;
      }
    },
    value(val) {
      if (val) {
        const execs = Array.isArray(this.job?.executions) ? this.job.executions : [];
        this.selectedAttempt = execs.length ? execs.length : 1;
      }
    }
  },
  computed: {
    dialog: { get() { return this.value; }, set(v) { this.$emit('input', v); } },
    tCopy() { return this.$t('copy') || 'Copier'; },

    isWorkflow() {
      const type = (this.job && this.job.type) ? this.job.type.toString().toLowerCase() : '';
      return type === 'workflow';
    },
    executionAttemptsCount() {
      const executions = Array.isArray(this.job?.executions) ? this.job.executions : [];
      return executions.length || 1;
    },

    currentExecutionId() {
      const executions = Array.isArray(this.job?.executions) ? this.job.executions : [];
      if (!executions.length) return null;
      const idx = Math.max(0, Math.min(this.selectedAttempt - 1, executions.length - 1));
      return executions[idx]?.id || null;
    },
    currentExec() {
      const executions = Array.isArray(this.job?.executions) ? this.job.executions : [];
      if (!executions.length) return null;
      const idx = Math.max(0, Math.min(this.selectedAttempt - 1, executions.length - 1));
      return executions[idx] || null;
    },
    unitaryInputContext() { return this.job?.initialContext || {}; },
    unitaryOutputContext() { return this.currentExec?.outputContext || {}; },

    // Durées unitaire (mêmes règles que transition)
    durationSec() {
      const s = this.parseDate(this.currentExec && (this.currentExec.startedAt || this.currentExec.createdAt));
      const f = this.parseDate(this.currentExec && this.currentExec.finishedAt);
      if (!s || !f) return null;
      return Math.max(0, Math.round((f - s) / 1000));
    },
    waitTimeSec() {
      const c = this.parseDate(this.currentExec && this.currentExec.createdAt);
      const s = this.parseDate(this.currentExec && this.currentExec.startedAt);
      if (!c || !s) return null;
      return Math.max(0, Math.round((s - c) / 1000));
    },

    unitaryTabs() {
      return [
        { id: 'context', label: this.$t('job-manager.context') },
        { id: 'logs',    label: this.$t('job-manager.logs') },
        { id: 'raw',     label:  'Brut' }
      ];
    },

    headerItem() {
      const job = this.job || {};
      const executions = Array.isArray(job.executions) ? job.executions : [];
      const status = (job.status || '').toString().toLowerCase();
      const definitionName = (job.jobDefinition?.name) || (job.definition?.name) || '';
      const createdAt = job.createdAt || job.created_at || null;
      const user = job.createdBy || job.user || null;
      const configuredMaxAttempts = job.jobDefinition?.retryConfig?.strategy?.maxAttempts || null;

      if (this.isWorkflow && executions.length) {
        const startedAt = executions[0].startedAt || executions[0].createdAt || null;
        const finishedAt = executions[executions.length - 1].finishedAt || null;
        const durationSum = executions.reduce((sum, e) => sum + (typeof e?.duration === 'number' ? e.duration : 0), 0);
        const transports = Array.from(
            executions.reduce((set, e) => { (Array.isArray(e?.transports) ? e.transports : []).forEach(t => set.add(t)); return set; }, new Set())
        );
        return {
          id: job.id, definition: definitionName, type: job.type, status, user, created_at: createdAt,
          attempts: null, max_attempts: configuredMaxAttempts,
          duration: durationSum ? `${durationSum}s` : null,
          execution_started_at: startedAt, execution_finished_at: finishedAt, transports
        };
      }

      // Unitary
      const idx = Math.max(0, Math.min(this.selectedAttempt - 1, executions.length - 1));
      const selectedExec = executions[idx] || null;
      const execStartedAt = selectedExec?.startedAt || selectedExec?.createdAt || null;
      const execFinishedAt = selectedExec?.finishedAt || null;
      const transports = Array.isArray(selectedExec?.transports) ? selectedExec.transports : [];
      const execDuration = (typeof selectedExec?.duration !== 'undefined' && selectedExec?.duration !== null)
          ? `${selectedExec.duration}s` : null;

      return {
        id: job.id, definition: definitionName, type: job.type, status, user, created_at: createdAt,
        attempts: executions.length ? this.selectedAttempt : 0,
        max_attempts: configuredMaxAttempts,
        duration: execDuration, execution_started_at: execStartedAt, execution_finished_at: execFinishedAt,
        transports
      };
    },

    // Steps workflow (inchangé dans l’esprit)
    transitions() {
      if (!this.isWorkflow) return [];
      const job = this.job || {};
      const executions = Array.isArray(job.executions) ? job.executions : [];
      const places = Array.isArray(job.jobDefinition?.places) ? job.jobDefinition.places : [];

      const execById = {}; executions.forEach(e => { if (e?.id) execById[e.id] = e; });
      const placesDefByName = {}; places.forEach(p => { if (p?.name) placesDefByName[p.name] = p; });

      const execsByPlace = {};
      executions.forEach(e => { const pl = e?.place || null; if (!pl) return; (execsByPlace[pl] ||= []).push(e); });

      const latestExecByPlace = {};
      const getTime = e => e?.finishedAt || e?.startedAt || e?.createdAt || null;
      Object.keys(execsByPlace).forEach(place => {
        const list = execsByPlace[place].slice().sort((a, b) => {
          const attA = Number.isFinite(a?.attempts) ? a.attempts : 0;
          const attB = Number.isFinite(b?.attempts) ? b.attempts : 0;
          if (attA !== attB) return attB - attA;
          const ta = getTime(a), tb = getTime(b);
          if (!ta && !tb) return 0; if (!ta) return 1; if (!tb) return -1;
          return new Date(tb) - new Date(ta);
        });
        latestExecByPlace[place] = list[0];
      });

      const transitionsDef = Array.isArray(job.jobDefinition?.transitions) ? job.jobDefinition.transitions : [];
      const toByFrom = {}, transNameMap = {}, inbound = {}, prevsByTo = {};
      transitionsDef.forEach(tr => {
        const fromArr = Array.isArray(tr.from) ? tr.from : [];
        const toArr = Array.isArray(tr.to) ? tr.to : [];
        fromArr.forEach(f => {
          toArr.forEach(t => {
            (toByFrom[f] ||= []).push(t);
            transNameMap[`${f}=>${t}`] = tr.name || null;
            inbound[t] = (inbound[t] || 0) + 1;
            (prevsByTo[t] ||= []).push(f);
          });
        });
      });

      const nodes = [], indegree = {}, placeOrderIndex = {};
      (places || []).forEach((p, idx) => {
        if (!p?.name || p.name === 'idle') return;
        nodes.push(p.name); indegree[p.name] = inbound[p.name] || 0; placeOrderIndex[p.name] = idx;
      });

      const depth = {};
      let queue = nodes.filter(n => (indegree[n] || 0) === 0).sort((a,b)=>placeOrderIndex[a]-placeOrderIndex[b]);
      queue.forEach(n => { depth[n] = 0; });
      const orderedPlaces = [];
      while (queue.length) {
        const n = queue.shift(); if (!n) break; orderedPlaces.push(n);
        (toByFrom[n] || []).forEach(to => {
          if (to === 'idle' || typeof indegree[to] === 'undefined') return;
          const nd = (depth[n] || 0) + 1;
          depth[to] = Math.max(typeof depth[to] === 'number' ? depth[to] : 0, nd);
          indegree[to] = (indegree[to] || 0) - 1;
          if (indegree[to] === 0) { queue.push(to); queue.sort((a,b)=>placeOrderIndex[a]-placeOrderIndex[b]); }
        });
      }

      (places || []).forEach(p => {
        if (!p?.name || p.name === 'idle') return;
        if (!orderedPlaces.includes(p.name)) { orderedPlaces.push(p.name); if (typeof depth[p.name] !== 'number') depth[p.name] = 0; }
      });

      const steps = [];
      orderedPlaces.forEach(place => {
        const exec = latestExecByPlace[place] || null;
        const prevPlaces = (prevsByTo[place] || []).filter(x => x && x !== 'idle');
        if (exec) {
          let prevPlace = null;
          if (exec.fromExecutionId && execById[exec.fromExecutionId]) prevPlace = execById[exec.fromExecutionId].place || null;
          else if (prevPlaces?.length) {
            const executedPrev = [...steps].reverse().find(s => prevPlaces.includes(s.place));
            prevPlace = executedPrev ? executedPrev.place : prevPlaces[0];
          }
          steps.push({
            id: exec.id, executionId: exec.id, fromExecutionId: exec.fromExecutionId || null,
            name: place, duration: exec.duration != null ? `${exec.duration}s` : null,
            startedAt: exec.startedAt || exec.createdAt || null, finishedAt: exec.finishedAt || null,
            attempts: exec.attempts || 1, transports: exec.transports || [],
            jobClassName: placesDefByName[place]?.jobClassName || null, place,
            inputContext: exec.inputContext || {}, outputContext: exec.outputContext || {},
            status: (exec.status || '').toString().toLowerCase(),
            prevPlace, prevPlaces, depth: typeof depth[place] === 'number' ? depth[place] : 0,
            transitionName: prevPlace ? (transNameMap[`${prevPlace}=>${place}`] || null) : null,
            notExecuted: false, hadFailures: (execsByPlace[place] || []).some(x => (x.status || '').toString().toLowerCase() === 'failed')
          });
        } else {
          const prefPrev = (prevPlaces && prevPlaces.length) ? prevPlaces[0] : null;
          steps.push({
            id: `pending-${place}`, name: place, duration: null, startedAt: null, finishedAt: null,
            attempts: 0, transports: placesDefByName[place]?.transports || [], jobClassName: placesDefByName[place]?.jobClassName || null,
            place, inputContext: {}, outputContext: {}, status: null, prevPlace: prefPrev, prevPlaces,
            depth: typeof depth[place] === 'number' ? depth[place] : 0, transitionName: prefPrev ? (transNameMap[`${prefPrev}=>${place}`] || null) : null,
            notExecuted: true
          });
        }
      });
      return steps;
    }
  },
  methods: {
    close() { this.dialog = false; },
    parseDate(d) { try { return d ? new Date(d) : null; } catch { return null; } },
    formatDateTime(d) {
      if (!d) return null;
      try {
        const dt = typeof d === 'string' ? new Date(d) : d;
        if (!dt || isNaN(dt.getTime())) return null;
        const p = (n)=>String(n).padStart(2,'0');
        return `${dt.getFullYear()}-${p(dt.getMonth()+1)}-${p(dt.getDate())} ${p(dt.getHours())}:${p(dt.getMinutes())}:${p(dt.getSeconds())}`;
      } catch { return null; }
    },
    humanDuration(sec) {
      if (sec == null) return null;
      if (sec < 60) return `${sec}s`;
      const m = Math.floor(sec/60), s = sec%60; if (m < 60) return `${m}m ${s}s`;
      const h = Math.floor(m/60), mm = m%60; return `${h}h ${mm}m ${s}s`;
    },
    truncateId(id) { const s = (id || '') + ''; return s.length > 16 ? `${s.slice(0,8)}…${s.slice(-6)}` : s; },
    async copy(text) {
      if (!text) return;
      try { await navigator.clipboard.writeText(String(text)); } catch {
        const ta = document.createElement('textarea'); ta.value = String(text);
        document.body.appendChild(ta); ta.select(); try { document.execCommand('copy'); } catch {
          // fallback

        }
        document.body.removeChild(ta);
      }
    },
    showTransitionDetails(transition) {
      const job = this.job || {};
      const executions = Array.isArray(job.executions) ? job.executions : [];
      const place = transition.place || transition.name;
      const samePlaceExecs = executions.filter(e => (e?.place || null) === place);
      samePlaceExecs.sort((a,b)=>{
        const at=a?.startedAt||a?.createdAt||null; const bt=b?.startedAt||b?.createdAt||null;
        if(!at && !bt) return 0; if(!at) return 1; if(!bt) return -1; return new Date(at)-new Date(bt);
      });
      const places = (job.jobDefinition && Array.isArray(job.jobDefinition.places)) ? job.jobDefinition.places : [];
      const placeDef = places.find(p => p?.name === place) || {};
      this.selectedTransition = { name: transition.name || place, place, executions: samePlaceExecs, jobClassName: placeDef.jobClassName || null };
      this.showTransitionModal = true;
    }
  }
};
</script>

<style lang="scss" scoped>
.detail-item { display:flex; margin-bottom:10px;
  .detail-label { font-weight:bold; min-width:140px; }
  .detail-value { flex:1; display:flex; align-items:center;
    .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size:12px; background:#f3f3f3; padding:2px 6px; border-radius:4px; margin-right:6px; }
  }
}
</style>
